/**
 * NativeEmailClient - Client email natif style Outlook
 * Backend: /api/webmail (IMAP direct)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail, Inbox, Send, Trash2, Archive, Star, RefreshCw,
  ChevronRight, ChevronDown, Paperclip, Search, Plus,
  Sparkles, FileText, X, Loader2, AlertCircle,
  Reply, Forward, FolderOpen, Eye, EyeOff, MailOpen,
  Download, Settings, Zap, ArrowLeft, MoreHorizontal,
  Flag, Check, Move, Play, Link, Upload, PenLine, Save, FolderPlus
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Textarea } from '../../../components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubContent, DropdownMenuSubTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../../../components/ui/alert-dialog';
import { toast } from 'sonner';
import EmailTriggersManager from './EmailTriggersManager';
import { enteteTenant } from "../../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL || '';

// ===== ICÔNES PAR TYPE =====
const FOLDER_ICONS = {
  inbox: Inbox, sent: Send, drafts: FileText, trash: Trash2,
  junk: AlertCircle, archive: Archive, default: FolderOpen
};

// ===== SIDEBAR DOSSIERS (Arbre dépliable) =====
const SYSTEM_FOLDERS = new Set(['INBOX', 'Sent', 'Drafts', 'Trash', 'Junk', 'Spam']);

const FolderTreeItem = ({ folder, children, isSelected, onSelect, depth = 0, onRenameFolder, onDeleteFolder, onCreateSubfolder }) => {
  const [expanded, setExpanded] = useState(depth <= 1 && (folder.special === 'archive' || children?.length > 0));
  const hasChildren = children && children.length > 0;
  const Icon = FOLDER_ICONS[folder.special] || FOLDER_ICONS.default;
  const isSystem = SYSTEM_FOLDERS.has((folder.name || '').toUpperCase().split('.').pop()) || folder.special;

  return (
    <div>
      <div
        onClick={() => onSelect(folder.name)}
        rôle="button"
        tabIndex={0}
        data-testid={`folder-${folder.display_name}`}
        className={`group w-full flex items-center gap-1.5 py-1.5 pr-1 rounded transition-colors text-[13px] cursor-pointer ${
          isSelected
            ? 'bg-blue-600/20 text-blue-400 font-medium'
            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {hasChildren ? (
          <span
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-slate-500 hover:text-slate-700 cursor-pointer"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </span>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="flex-1 text-left truncate">{folder.display_name}</span>
        {(folder.unread > 0 || folder.total > 0) && (
          <span
            className="flex items-center gap-1 text-[11px]"
            title={`${folder.unread || 0} non lus / ${folder.total ?? 0} au total`}
            data-testid={`folder-count-${folder.display_name}`}
          >
            {folder.unread > 0 && (
              <span className="font-semibold text-blue-500 bg-blue-500/15 px-1.5 rounded-full">{folder.unread}</span>
            )}
            <span className="text-slate-400">{folder.unread > 0 ? `/ ${folder.total ?? 0}` : folder.total}</span>
          </span>
        )}
        {(onCreateSubfolder || (!isSystem && (onRenameFolder || onDeleteFolder))) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200"
                aria-label={`Actions ${folder.display_name}`}
                data-testid={`folder-menu-${folder.display_name}`}
              >
                <MoreHorizontal className="w-3.5 h-3.5 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {onCreateSubfolder && (
                <DropdownMenuItem onClick={() => onCreateSubfolder(folder)} className="cursor-pointer text-xs">
                  <FolderPlus className="w-3.5 h-3.5 mr-2" /> Nouveau sous-dossier
                </DropdownMenuItem>
              )}
              {!isSystem && onRenameFolder && (
                <DropdownMenuItem onClick={() => onRenameFolder(folder)} className="cursor-pointer text-xs">
                  <PenLine className="w-3.5 h-3.5 mr-2" /> Renommer
                </DropdownMenuItem>
              )}
              {!isSystem && onRenameFolder && (
                <DropdownMenuItem onClick={() => onRenameFolder(folder, { mode: 'move' })} className="cursor-pointer text-xs">
                  <Move className="w-3.5 h-3.5 mr-2" /> Déplacer
                </DropdownMenuItem>
              )}
              {!isSystem && onDeleteFolder && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDeleteFolder(folder)}
                    className="cursor-pointer text-xs text-red-500 focus:text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Supprimer
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {expanded && hasChildren && (
        <div>{children}</div>
      )}
    </div>
  );
};

const buildFolderTree = (folders) => {
  // Group by parent
  const byParent = {};
  const roots = [];
  for (const f of folders) {
    if (!f.parent) {
      roots.push(f);
    } else {
      if (!byParent[f.parent]) byParent[f.parent] = [];
      byParent[f.parent].push(f);
    }
  }
  return { roots, byParent };
};

const FolderTree = ({ folders, selectedFolder, onSelectFolder, onRenameFolder, onDeleteFolder, onCreateSubfolder }) => {
  const { roots, byParent } = buildFolderTree(folders);

  const renderNode = (folder, depth) => {
    const children = byParent[folder.full_path] || [];
    return (
      <FolderTreeItem
        key={folder.name}
        folder={folder}
        isSelected={selectedFolder === folder.name}
        onSelect={onSelectFolder}
        depth={depth}
        onRenameFolder={onRenameFolder}
        onDeleteFolder={onDeleteFolder}
        onCreateSubfolder={onCreateSubfolder}
      >
        {children.map(child => renderNode(child, depth + 1))}
      </FolderTreeItem>
    );
  };

  // Ordre : INBOX d'abord, puis spéciaux, puis le reste
  const order = ['inbox', 'sent', 'drafts', 'trash', 'junk', 'archive'];
  const specials = roots.filter(f => f.special).sort((a, b) =>
    order.indexOf(a.special) - order.indexOf(b.special)
  );
  const others = roots.filter(f => !f.special);

  return (
    <div className="space-y-0.5">
      {[...specials, ...others].map(f => renderNode(f, 0))}
    </div>
  );
};

const FolderSidebar = ({ folders, selectedFolder, onSelectFolder, loading, onShowTriggers, showTriggersActive, onRefreshFolders, onShowSignature, signatureActive, mailboxes, activeConnectionId, onSwitchMailbox, onCreateFolder, onRenameFolder, onDeleteFolder, onCreateSubfolder }) => (
  /* Mobile : hauteur DÉTERMINÉE 50vh pour que flex-1 + min-h-0 fonctionnent
     correctement (max-h seul n'impose pas une hauteur, il limite — donc
     flex-1 ne pouvait pas se contraindre et le scroll interne ne se
     déclenchait pas).
     Desktop (md+) : largeur fixe 52, hauteur naturelle (héritée du
     parent h-full). */
  <div className="w-full md:w-52 border-r border-slate-200 flex flex-col bg-white/40 h-[50vh] md:h-auto flex-shrink-0 overflow-hidden">
    {/* Sélecteur de boîte mail (affiché seulement si multi-boîtes) */}
    {mailboxes && mailboxes.length > 1 && (
      <div className="px-2 py-1.5 border-b border-slate-200">
        <select
          value={activeConnectionId || ''}
          onChange={(e) => onSwitchMailbox(e.target.value || null)}
          className="w-full bg-slate-100 text-xs text-slate-800 rounded px-2 py-1.5 border border-slate-300 focus:border-blue-500 focus:outline-none"
          data-testid="mailbox-selector"
        >
          {mailboxes.map(box => (
            <option key={box.id} value={box.id}>{box.email || box.name}</option>
          ))}
        </select>
      </div>
    )}
    <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Dossiers</span>
      <div className="flex items-center gap-1">
        {loading && <Loader2 className="w-3 h-3 animate-spin text-blue-400" />}
        <button onClick={onCreateFolder} className="text-slate-400 hover:text-green-400 transition-colors" title="Créer un dossier" data-testid="create-folder-btn">
          <FolderPlus className="w-3 h-3" />
        </button>
        <button onClick={onRefreshFolders} className="text-slate-400 hover:text-slate-700 transition-colors" title="Rafraîchir dossiers">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
    </div>
    {/* min-h-0 sur le scroll : sans lui, flex-1 garde la hauteur
        naturelle du contenu et désactive le scroll interne.
        overflow-y-scroll force la barre de scroll en mobile (auto
        peut être ignoré par certains browsers iOS).
        touch-pan-y indique au browser que le scroll vertical est
        attendu — évite que le scroll soit absorbé par le parent. */}
    <div className="flex-1 min-h-0 overflow-y-scroll overscroll-contain touch-pan-y"
         style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="py-1 px-1">
        {folders.length === 0 && !loading ? (
          <p className="text-xs text-slate-400 text-center py-6">Aucun dossier</p>
        ) : (
          <FolderTree folders={folders} selectedFolder={selectedFolder} onSelectFolder={onSelectFolder} onRenameFolder={onRenameFolder} onDeleteFolder={onDeleteFolder} onCreateSubfolder={onCreateSubfolder} />
        )}
      </div>
    </div>
    <div className="p-1.5 border-t border-slate-200 space-y-0.5">
      <button
        onClick={onShowTriggers}
        data-testid="triggers-btn"
        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded text-xs transition-colors ${
          showTriggersActive
            ? 'bg-amber-500/15 text-amber-400'
            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
        }`}
      >
        <Zap className="w-3.5 h-3.5" />
        <span>Automatisations</span>
      </button>
      <button
        onClick={onShowSignature}
        data-testid="signature-btn"
        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded text-xs transition-colors ${
          signatureActive
            ? 'bg-blue-500/15 text-blue-400'
            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
        }`}
      >
        <PenLine className="w-3.5 h-3.5" />
        <span>Signature</span>
      </button>
    </div>
  </div>
);

// ===== LISTE DES MESSAGES =====
const MessageList = ({
  messages, selectedMessage, onSelectMessage, loading,
  folder, folderDisplay, onRefresh, onCompose, searchQuery, setSearchQuery, onSearch,
  page, totalMessages, onPageChange
}) => {
  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts * 1000);
    const now = new Date();
    return d.toDateString() === now.toDateString()
      ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery?.trim()) onSearch(searchQuery.trim());
  };

  const totalPages = Math.ceil(totalMessages / 50);

  return (
    <div className="w-full md:w-80 border-r border-slate-200 flex flex-col bg-slate-50/50 min-w-0 h-full overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{folderDisplay || folder}</h3>
          <span className="text-[11px] text-slate-500">{totalMessages} messages</span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" onClick={onCompose} className="h-7 w-7 p-0" data-testid="compose-btn">
            <Plus className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onRefresh} className="h-7 w-7 p-0" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="p-2 border-b border-slate-200">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input
            placeholder="Rechercher..."
            value={searchQuery || ''}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-7 pl-8 pr-8 bg-slate-100 border-slate-300 text-xs"
            data-testid="search-input"
          />
          {searchQuery && (
            <button type="button" onClick={() => { setSearchQuery(''); onRefresh(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="w-3.5 h-3.5 text-slate-500 hover:text-slate-700" />
            </button>
          )}
        </div>
      </form>

      {/* Messages — voir commentaire FolderSidebar : ScrollArea remplacé
          par div natif avec min-h-0 + touch-pan-y pour scroll iOS. */}
      <div className="flex-1 min-h-0 overflow-y-scroll overscroll-contain touch-pan-y"
           style={{ WebkitOverflowScrolling: 'touch' }}>
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-xs">Aucun message</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/40">
            {messages.map(msg => {
              const isSelected = selectedMessage?.uid === msg.uid;
              const isUnread = !msg.flags?.seen;
              return (
                <button
                  key={msg.uid}
                  onClick={() => onSelectMessage(msg)}
                  data-testid={`message-${msg.uid}`}
                  className={`w-full px-3 py-2.5 text-left transition-colors ${
                    isSelected
                      ? 'bg-blue-500/10 border-l-2 border-blue-500'
                      : 'hover:bg-slate-50 border-l-2 border-transparent'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {isUnread && <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />}
                    {!isUnread && <div className="w-1.5 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[13px] truncate ${isUnread ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                          {msg.from?.name || msg.from?.email || 'Inconnu'}
                        </span>
                        <span className="text-[11px] text-slate-500 flex-shrink-0">{formatDate(msg.timestamp)}</span>
                      </div>
                      <p className={`text-[12px] truncate mt-0.5 ${isUnread ? 'font-medium text-slate-800' : 'text-slate-500'}`}>
                        {msg.subject || '(Sans sujet)'}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1">
                        {msg.has_attachment && <Paperclip className="w-3 h-3 text-slate-400" />}
                        {msg.flags?.flagged && <Star className="w-3 h-3 text-amber-400 fill-amber-400" />}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-2 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="h-6 px-2 text-xs">
            Préc.
          </Button>
          <span>{page}/{totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} className="h-6 px-2 text-xs">
            Suiv.
          </Button>
        </div>
      )}
    </div>
  );
};

// ===== VUE MESSAGE (Lecture + Actions) =====
const MessageView = ({
  message, fullMessage, loading, onBack, onReply, onForward,
  onDelete, onMarkRead, onFlag, onMoveMessage, folders, currentFolder,
  onAIAction, aiLoading, aiResult,
  emailActions, actionsLoading, onExecuteAction, onDismissActions,
  onDownloadAttachment,
}) => {
  const formatFullDate = (ts) => {
    if (!ts) return '';
    return new Date(ts * 1000).toLocaleString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!message) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50/50">
        <div className="text-center">
          <MailOpen className="w-14 h-14 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Sélectionnez un message</p>
        </div>
      </div>
    );
  }

  const msg = fullMessage || message;
  const fromName = msg.from?.name || msg.from?.email || 'Inconnu';
  const fromEmail = msg.from?.email || '';
  const toList = Array.isArray(msg.to) ? msg.to : [msg.to].filter(Boolean);
  const ccList = msg.cc || [];

  // Dossiers déplaçables (pas le dossier courant)
  const moveTargets = folders.filter(f => f.name !== currentFolder);

  return (
    <div className="flex-1 flex flex-col bg-slate-50/50 min-w-0">
      {/* Toolbar */}
      <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-1 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="h-7 w-7 p-0 md:hidden">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => onReply('reply')} className="h-7 text-xs" data-testid="reply-btn">
          <Reply className="w-3.5 h-3.5 mr-1" /> Répondre
        </Button>
        <Button variant="outline" size="sm" onClick={() => onReply('reply-all')} className="h-7 text-xs">
          <Reply className="w-3.5 h-3.5 mr-1" /> Rép. tous
        </Button>
        <Button variant="outline" size="sm" onClick={() => onForward()} className="h-7 text-xs">
          <Forward className="w-3.5 h-3.5 mr-1" /> Transférer
        </Button>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <Button variant="ghost" size="sm" onClick={() => onMarkRead(!msg.flags?.seen)} className="h-7 text-xs" title={msg.flags?.seen ? 'Marquer non lu' : 'Marquer lu'}>
          {msg.flags?.seen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </Button>
        <Button variant="ghost" size="sm" onClick={onFlag} className="h-7 text-xs" title="Important">
          <Flag className={`w-3.5 h-3.5 ${msg.flags?.flagged ? 'text-amber-400 fill-amber-400' : ''}`} />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="h-7 text-xs text-red-400 hover:text-red-300" data-testid="delete-btn">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>

        {/* Menu Déplacer */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 text-xs">
              <Move className="w-3.5 h-3.5 mr-1" /> Déplacer
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white border-slate-300 max-h-64 overflow-y-auto">
            {moveTargets.map(f => (
              <DropdownMenuItem key={f.name} onClick={() => onMoveMessage(f.name)} className="cursor-pointer text-xs">
                {f.display_name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex-1" />

        {/* IA */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs bg-violet-500/10 border-violet-500/30 text-violet-400" data-testid="ai-menu-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> IA
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white border-slate-300 w-52">
            <DropdownMenuItem onClick={() => onAIAction('summarize')} className="cursor-pointer text-xs">
              <Eye className="w-3.5 h-3.5 mr-2" /> Résumer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAIAction('suggest-reply')} className="cursor-pointer text-xs">
              <Reply className="w-3.5 h-3.5 mr-2" /> Suggérer réponse
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAIAction('extract-info')} className="cursor-pointer text-xs">
              <FileText className="w-3.5 h-3.5 mr-2" /> Extraire infos
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAIAction('categorize')} className="cursor-pointer text-xs">
              <FolderOpen className="w-3.5 h-3.5 mr-2" /> Catégoriser
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAIAction('analyze_business')} className="cursor-pointer text-xs">
              <Sparkles className="w-3.5 h-3.5 mr-2" /> Analyse métier
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Header message */}
      <div className="px-5 py-3 border-b border-slate-200">
        <h2 className="text-base font-semibold text-slate-900 mb-2">{msg.subject || '(Sans sujet)'}</h2>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-600/80 flex items-center justify-center text-slate-900 text-sm font-medium flex-shrink-0">
            {fromName[0]?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-900">{fromName}</span>
              <span className="text-xs text-slate-700">&lt;{fromEmail}&gt;</span>
            </div>
            <div className="text-xs text-slate-700 mt-0.5">
              À : {toList.map(t => t?.name || t?.email || '').join(', ')}
              {ccList.length > 0 && <span> — Cc : {ccList.map(c => c?.name || c?.email).join(', ')}</span>}
            </div>
          </div>
          <span className="text-xs text-slate-600 flex-shrink-0">{formatFullDate(msg.timestamp)}</span>
        </div>

        {/* Pièces jointes (AG-04 #9 — download via fetch + headers) */}
        {msg.attachments?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {msg.attachments.map((att, i) => (
              <button
                key={i}
                type="button"
                onClick={() =>
                  onDownloadAttachment?.(msg.uid, currentFolder, i, att.filename)
                }
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded text-xs text-slate-700 transition-colors cursor-pointer"
                data-testid={`download-attachment-${i}`}
                aria-label={`Télécharger ${att.filename}`}
              >
                <Download className="w-3 h-3" />
                <span className="truncate max-w-[140px]">{att.filename}</span>
                {att.size > 0 && <span className="text-slate-500">{Math.round(att.size / 1024)}Ko</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Panneau Actions IA détectées */}
      {(emailActions?.proposed_actions?.length > 0 || actionsLoading) && (
        <ActionPanel
          actions={emailActions?.proposed_actions}
          loading={actionsLoading}
          onExecute={onExecuteAction}
          onDismiss={onDismissActions}
          extractedData={emailActions?.extracted_data}
          matchedDossier={emailActions?.matched_dossier}
        />
      )}

      {/* Résultat IA */}
      {aiResult && (
        <div className="mx-5 mt-3 p-3 bg-violet-500/5 border border-violet-500/20 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-violet-400 uppercase">
              {aiResult.action === 'summarize' ? 'Résumé' :
               aiResult.action === 'suggest-reply' ? 'Suggestion de réponse' :
               aiResult.action === 'extract-info' ? 'Infos extraites' :
               aiResult.action === 'categorize' ? 'Catégorie' :
               aiResult.action === 'analyze_business' ? 'Analyse métier' : 'IA'}
            </span>
            {aiLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400" />}
          </div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap">
            {aiResult.result || aiResult.summary || aiResult.reply || aiResult.analysis || JSON.stringify(aiResult.data, null, 2)}
          </div>
          {aiResult.action === 'suggest-reply' && aiResult.reply && (
            <Button size="sm" className="mt-2 bg-violet-600 hover:bg-violet-700 text-xs"
              onClick={() => onReply('reply', aiResult.reply)}>
              Utiliser cette réponse
            </Button>
          )}
        </div>
      )}

      {/* Corps */}
      <ScrollArea className="flex-1">
        <div className="px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
            </div>
          ) : msg.body_html ? (
            <div
              className="prose prose-invert prose-sm max-w-none [&_a]:text-blue-400 [&_img]:max-w-full"
              dangerouslySetInnerHTML={{ __html: msg.body_html }}
            />
          ) : msg.body_text && msg.body_text.trim().startsWith('<') ? (
            <div
              className="prose prose-invert prose-sm max-w-none [&_a]:text-blue-400 [&_img]:max-w-full"
              dangerouslySetInnerHTML={{ __html: msg.body_text }}
            />
          ) : msg.body_text ? (
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{msg.body_text}</pre>
          ) : (
            <p className="text-slate-500 text-sm italic">Contenu non disponible</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

// ===== PANNEAU D'ACTIONS EMAIL (IA) =====
const ActionPanel = ({ actions, loading, onExecute, onDismiss, extractedData, matchedDossier }) => {
  if (!actions?.length && !loading) return null;

  const typeColors = {
    update_dossier_status: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    update_dossier_field: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    link_email_to_dossier: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
    draft_reply: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    open_anah_portal: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
  };

  const typeIcons = {
    update_dossier_status: Check,
    update_dossier_field: FileText,
    link_email_to_dossier: Link,
    draft_reply: Reply,
    open_anah_portal: FolderOpen,
  };

  return (
    <div className="mx-5 mt-3 p-3 bg-slate-100 border border-slate-300 rounded-lg" data-testid="action-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Actions détectées</span>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-amber-400" />}
        </div>
        <button onClick={onDismiss} className="text-slate-500 hover:text-slate-700">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Résumé IA */}
      {extractedData?.resume && (
        <p className="text-xs text-slate-600 mb-2 italic">{extractedData.resume}</p>
      )}

      {/* Dossier matché */}
      {matchedDossier && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-cyan-500/5 border border-cyan-500/20 rounded text-xs">
          <FolderOpen className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-cyan-300">Dossier lié : <strong>{matchedDossier.référence}</strong> — {matchedDossier.client}</span>
        </div>
      )}

      {/* Liste des actions */}
      <div className="space-y-1.5">
        {actions?.map((action, i) => {
          const Icon = typeIcons[action.type] || Zap;
          const colorClass = typeColors[action.type] || 'bg-slate-200 border-zinc-600 text-slate-700';
          return (
            <div key={i} className={`flex items-center gap-2 px-2.5 py-2 rounded border ${colorClass}`}>
              <Icon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="flex-1 text-xs">{action.label}</span>
              {action.auto_executable && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                  onClick={() => onExecute(action, i)}
                  data-testid={`execute-action-${i}`}>
                  <Play className="w-3 h-3 mr-1" /> Appliquer
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ===== MODAL SIGNATURE =====
const SignatureModal = ({ isOpen, onClose, signature, onSave }) => {
  const [name, setName] = useState('Ma signature');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && signature) {
      setName(signature.name || 'Ma signature');
      setContent(signature.content || '');
    }
  }, [isOpen, signature]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    await onSave({ name, content, is_default: true });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg rounded-xl border border-slate-300 bg-white shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <PenLine className="w-4 h-4 text-blue-400" />
            <span className="font-semibold text-slate-900 text-sm">Signature email</span>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Nom de la signature</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-1.5 rounded text-sm bg-slate-100 border border-slate-300 text-slate-900 focus:outline-none focus:border-blue-500"
              placeholder="Ex: Ma signature professionnelle"
              data-testid="signature-name-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Contenu (texte brut)</label>
            <Textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={8}
              className="bg-slate-100 border-slate-300 text-slate-900 text-sm font-mono resize-none focus:border-blue-500"
              placeholder={"Cordialement,\nPrénom NOM\nTitre | Entreprise\n📞 +33 X XX XX XX XX\n✉ email@exemple.fr"}
              data-testid="signature-content-input"
            />
            <p className="text-xs text-slate-400 mt-1">{content.length} caractères — apparaîtra automatiquement dans vos nouveaux emails</p>
          </div>
          {content && (
            <div className="p-3 rounded bg-slate-100 border border-slate-300">
              <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Aperçu</p>
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans">{content}</pre>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 pb-4">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-xs text-slate-600 hover:text-slate-800 border border-slate-300">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded text-xs font-medium bg-blue-600 text-slate-900 hover:bg-blue-500 disabled:opacity-50"
            data-testid="signature-save-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== MODAL COMPOSITION =====
const ComposeModal = ({ isOpen, onClose, onSend, replyTo, replyType, initialBody, signature, tenantId }) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (replyTo && replyType) {
      if (replyType === 'reply' || replyType === 'reply-all') {
        setTo(replyTo.from?.email || '');
        setSubject(`Re: ${(replyTo.subject || '').replace(/^Re:\s*/i, '')}`);
        setBody(''); // Reset body for reply - no signature injection
        if (replyType === 'reply-all' && replyTo.cc?.length) {
          setCc(replyTo.cc.map(c => c.email).join(', '));
          setShowCc(true);
        }
      } else if (replyType === 'forward') {
        setTo('');
        setSubject(`Fwd: ${replyTo.subject || ''}`);
        setBody(`\n\n--- Message transféré ---\nDe: ${replyTo.from?.email}\nObjet: ${replyTo.subject}\n\n${replyTo.body_text || replyTo.snippet || ''}`);
      }
    }
    if (initialBody) setBody(initialBody);
    // Injecter la signature uniquement pour un nouveau message (pas reply/forward)
    if (!replyTo && !initialBody && signature?.content) {
      setBody(`\n\n-- \n${signature.content}`);
    }
  }, [replyTo, replyType, initialBody, signature]);

  // Reset form on close
  useEffect(() => {
    if (!isOpen) {
      setAttachments([]);
    }
  }, [isOpen]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    // Limite: 10 fichiers, 25Mo max chacun
    const validFiles = files.filter(f => f.size <= 25 * 1024 * 1024);
    if (validFiles.length < files.length) {
      toast.error('Certains fichiers dépassent 25Mo');
    }
    setAttachments(prev => [...prev, ...validFiles].slice(0, 10));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!to.trim()) { toast.error('Destinataire requis'); return; }
    setSending(true);
    try {
      if (attachments.length > 0) {
        // Envoi avec pièces jointes (multipart form)
        const formData = new FormData();
        formData.append('to', to);
        formData.append('subject', subject);
        formData.append('body', body);
        if (cc) formData.append('cc', cc);
        attachments.forEach(f => formData.append('files', f));

        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/api/webmail/send-with-attachments`, {
          method: 'POST',
          headers: {
            ...enteteTenant(tenantId),
            ...(token && { "Authorization": `Bearer ${token}` })
          },
          body: formData
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Erreur envoi');
        }
        toast.success(`Message envoyé avec ${attachments.length} pièce(s) jointe(s)`);
      } else {
        // Envoi simple
        await onSend({ to, cc, subject, body, reply_to_uid: replyTo?.uid, reply_to_folder: replyTo?.folder });
        toast.success('Message envoyé');
      }
      onClose();
      setTo(''); setCc(''); setSubject(''); setBody(''); setAttachments([]);
    } catch (err) {
      toast.error(err.message || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="w-full max-w-2xl bg-white border border-slate-300 rounded-xl shadow-2xl">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 text-sm">
            {replyType === 'reply' ? 'Répondre' : replyType === 'reply-all' ? 'Répondre à tous' :
             replyType === 'forward' ? 'Transférer' : 'Nouveau message'}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <label className="w-10 text-xs text-slate-500">À</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="dest@email.com"
              className="flex-1 h-8 bg-slate-100 border-slate-300 text-sm" data-testid="compose-to" />
            {!showCc && <Button variant="ghost" size="sm" onClick={() => setShowCc(true)} className="h-7 text-xs">Cc</Button>}
          </div>
          {showCc && (
            <div className="flex items-center gap-2">
              <label className="w-10 text-xs text-slate-500">Cc</label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copie@email.com"
                className="flex-1 h-8 bg-slate-100 border-slate-300 text-sm" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="w-10 text-xs text-slate-500">Objet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet"
              className="flex-1 h-8 bg-slate-100 border-slate-300 text-sm" data-testid="compose-subject" />
          </div>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Votre message..."
            className="min-h-[200px] bg-slate-100 border-slate-300 text-sm" data-testid="compose-body" />
          
          {/* Pièces jointes */}
          <div className="border border-dashed border-slate-300 rounded-lg p-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500">Pièces jointes ({attachments.length}/10)</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="h-7 text-xs"
                data-testid="add-attachment-btn"
              >
                <Upload className="w-3.5 h-3.5 mr-1" /> Ajouter
              </Button>
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {attachments.map((file, i) => (
                  <div key={i} className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-700">
                    <Paperclip className="w-3 h-3" />
                    <span className="truncate max-w-[120px]">{file.name}</span>
                    <span className="text-slate-500">({Math.round(file.size / 1024)}Ko)</span>
                    <button onClick={() => removeAttachment(i)} className="ml-1 text-slate-500 hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button size="sm" onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="send-email-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Envoyer
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===== COMPOSANT PRINCIPAL =====
const NativeEmailClient = ({ tenantId = "tef" }) => {
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState('INBOX');
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [fullMessage, setFullMessage] = useState(null);
  const [fullMessageLoading, setFullMessageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyType, setReplyType] = useState(null);
  const [initialReplyBody, setInitialReplyBody] = useState('');
  const [showTriggers, setShowTriggers] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  // Actions IA extraites de l'email
  const [emailActions, setEmailActions] = useState(null);
  const [actionsLoading, setActionsLoading] = useState(false);
  // Signature
  const [signature, setSignature] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  // Multi-boîtes
  const [mailboxes, setMailboxes] = useState([]);
  const [activeConnectionId, setActiveConnectionId] = useState(null);
  // Ouverture ciblée (« Ouvrir dans le webmail » du Bureau de Matthieu) : { dossier, uid, message_id }, consommée UNE fois à l'ouverture.
  const cibleRef = useRef(null);
  const [boitesPretes, setBoitesPretes] = useState(false);
  if (cibleRef.current === null && typeof window !== 'undefined' && window.__webmailCible) {
    cibleRef.current = window.__webmailCible;
    window.__webmailCible = null;
  }
  // Création de dossier
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState('');
  const [parentLocked, setParentLocked] = useState(false);
  const [renameDialog, setRenameDialog] = useState(null); // { folder, mode: 'rename'|'move', newLeaf, newParent }
  const [renaming, setRenaming] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null); // folder
  const [deleting, setDeleting] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Query string helper pour connection_id
  const connParam = activeConnectionId ? `connection_id=${activeConnectionId}` : '';
  const appendConn = (url) => {
    if (!activeConnectionId) return url;
    return url + (url.includes('?') ? '&' : '?') + connParam;
  };

  const headers = useCallback(() => {
    const token = localStorage.getItem('session_token');
    return {
      ...enteteTenant(tenantId),
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  }, [tenantId]);

  // Charger la liste des boîtes mail
  const loadMailboxes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/webmail/boxes`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setMailboxes(data.boxes || []);
        // Sélectionner la première si pas encore choisie (en cas d'ouverture ciblée : la boîte amo@ d'abord)
        if (!activeConnectionId && data.boxes?.length > 0) {
          const amo = cibleRef.current && data.boxes.length > 1 ? data.boxes.find((b) => /^amo@/i.test(b.email || '')) : null;
          const choisie = amo ? amo.id : (data.boxes.length > 1 ? data.boxes[0].id : null);
          if (cibleRef.current) cibleRef.current = { ...cibleRef.current, connectionId: choisie };
          setActiveConnectionId(choisie);
        }
      }
    } catch (err) {
      console.error('Mailboxes error:', err);
    } finally {
      setBoitesPretes(true);
    }
  }, [headers, activeConnectionId]);

  // Load folders (avec cache)
  const loadFolders = useCallback(async (forceRefresh = false) => {
    setFoldersLoading(true);
    try {
      const url = appendConn(`${API}/api/webmail/folders${forceRefresh ? '?refresh=true' : ''}`);
      const res = await fetch(url, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setFolders(data.folders || []);
      }
    } catch (err) {
      console.error('Folders error:', err);
    } finally {
      setFoldersLoading(false);
    }
  }, [headers, activeConnectionId]);

  // Load messages
  const loadMessages = useCallback(async (folder = selectedFolder, p = page) => {
    setMessagesLoading(true);
    try {
      const res = await fetch(
        appendConn(`${API}/api/webmail/messages?folder=${encodeURIComponent(folder)}&page=${p}&limit=50`),
        { headers: headers() }
      );
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setTotalMessages(data.total || 0);
      }
    } catch (err) {
      console.error('Messages error:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, [selectedFolder, page, headers]);

  // Load full message
  const loadFullMessage = useCallback(async (uid, folder) => {
    setFullMessageLoading(true);
    setAiResult(null);
    setEmailActions(null);
    try {
      const url = appendConn(`${API}/api/webmail/message/${uid}?folder=${encodeURIComponent(folder)}`);
      const res = await fetch(url, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        const msg = data.message || data;
        setFullMessage(msg);
        // Mark as read
        if (!msg.flags?.seen) {
          fetch(`${API}/api/webmail/mark`, {
            method: 'POST',
            headers: { ...headers(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, folder, flag: 'seen', value: true })
          }).catch(() => {});
        }
        // Extraire les actions IA en background
        extractEmailActions(uid, folder);
      } else {
        console.error('[Webmail] Message fetch failed:', res.status);
      }
    } catch (err) {
      console.error('[Webmail] Message error:', err);
    } finally {
      setFullMessageLoading(false);
    }
  }, [headers]);

  // Extraction des actions IA depuis l'email
  const extractEmailActions = useCallback(async (uid, folder) => {
    setActionsLoading(true);
    try {
      const res = await fetch(
        `${API}/api/webmail/extract-actions/${uid}?folder=${encodeURIComponent(folder)}${activeConnectionId ? '&connection_id=' + activeConnectionId : ''}`,
        { method: 'POST', headers: headers() }
      );
      if (res.ok) {
        const data = await res.json();
        // Seulement afficher le panel si des actions sont proposées
        if (data.proposed_actions?.length > 0 || data.detected_type !== 'unknown') {
          setEmailActions(data);
        }
        // Notification si l'IA a auto-classé le mail
        if (data.auto_classified) {
          toast.success(`IA : email classé automatiquement dans ${data.auto_classified.label}`, {
            duration: 5000,
            icon: '📂',
          });
          // Rafraîchir les dossiers et messages car le mail a été déplacé
          loadFolders(true);
          loadMessages(selectedFolder, page);
        }
      }
    } catch (err) {
      console.error('[Webmail] Actions extraction error:', err);
    } finally {
      setActionsLoading(false);
    }
  }, [headers]);

  // Exécuter une action extraite
  const executeAction = useCallback(async (action, index) => {
    if (!selectedMessage || !emailActions) return;
    try {
      const res = await fetch(`${API}/api/webmail/execute-action`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          email_uid: selectedMessage.uid,
          email_folder: selectedFolder,
          matched_dossier_id: emailActions.matched_dossier?.id
        })
      });
      if (res.ok) {
        const result = await res.json();
        if (result.success) {
          toast.success(result.message || 'Action exécutée');
          // Retirer l'action exécutée de la liste
          setEmailActions(prev => ({
            ...prev,
            proposed_actions: prev.proposed_actions.filter((_, i) => i !== index)
          }));
          // Si c'est un classement, rafraîchir dossiers et messages
          if (action.type === 'classify_email') {
            loadFolders(true);
            loadMessages(selectedFolder, page);
            setSelectedMessage(null);
            setFullMessage(null);
          }
        } else {
          toast.error(result.error || 'Erreur');
        }
      }
    } catch (err) {
      toast.error('Erreur lors de l\'exécution');
    }
  }, [selectedMessage, selectedFolder, emailActions, headers]);

  // Search
  const handleSearch = useCallback(async (query) => {
    setMessagesLoading(true);
    try {
      const res = await fetch(
        `${API}/api/webmail/search?query=${encodeURIComponent(query)}&folder=${encodeURIComponent(selectedFolder)}`,
        { headers: headers() }
      );
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setTotalMessages(data.total || 0);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, [selectedFolder, headers]);

  // Send email
  const handleSend = async (email) => {
    const res = await fetch(`${API}/api/webmail/send`, {
      method: 'POST',
      headers: { ...headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify(email)
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Erreur envoi');
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!selectedMessage) return;
    try {
      const res = await fetch(appendConn(`${API}/api/webmail/delete`), {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: selectedMessage.uid, folder: selectedFolder })
      });
      if (res.ok) {
        toast.success('Message supprimé');
        setSelectedMessage(null);
        setFullMessage(null);
        loadMessages();
      } else {
        // T-105 : le serveur laisse le mail en place si la copie vers la corbeille échoue — on le dit.
        let cause = '';
        try { cause = (await res.json())?.detail || ''; } catch (e) { /* corps non JSON */ }
        toast.error(cause || 'Suppression non faite : le message est resté en place');
      }
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  // Mark read/unread
  const handleMarkRead = async (seen) => {
    if (!selectedMessage) return;
    try {
      await fetch(appendConn(`${API}/api/webmail/mark`), {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: selectedMessage.uid, folder: selectedFolder, flag: 'seen', value: seen })
      });
      setFullMessage(prev => prev ? { ...prev, flags: { ...prev.flags, seen } } : prev);
      setMessages(prev => prev.map(m =>
        m.uid === selectedMessage.uid ? { ...m, flags: { ...m.flags, seen } } : m
      ));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Flag
  const handleFlag = async () => {
    if (!selectedMessage) return;
    const newVal = !(fullMessage?.flags?.flagged || selectedMessage.flags?.flagged);
    try {
      await fetch(appendConn(`${API}/api/webmail/mark`), {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: selectedMessage.uid, folder: selectedFolder, flag: 'flagged', value: newVal })
      });
      setFullMessage(prev => prev ? { ...prev, flags: { ...prev.flags, flagged: newVal } } : prev);
      setMessages(prev => prev.map(m =>
        m.uid === selectedMessage.uid ? { ...m, flags: { ...m.flags, flagged: newVal } } : m
      ));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Move
  const handleMove = async (toFolder) => {
    if (!selectedMessage) return;
    try {
      const res = await fetch(appendConn(`${API}/api/webmail/move`), {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: selectedMessage.uid, from_folder: selectedFolder, to_folder: toFolder })
      });
      if (res.ok) {
        toast.success('Message déplacé');
        setSelectedMessage(null);
        setFullMessage(null);
        loadMessages();
      }
    } catch (err) {
      toast.error('Erreur déplacement');
    }
  };

  // AG-04 #9 — download d'une PJ depuis l'IMAP (envoie X-Tenant-ID + auth,
  // contrairement à un <a href> natif qui n'envoie pas les headers).
  const handleDownloadAttachment = async (uid, folderName, attachmentIndex, filename) => {
    try {
      const url = appendConn(
        `${API}/api/webmail/message/${uid}/attachment/${attachmentIndex}` +
        `?folder=${encodeURIComponent(folderName)}`
      );
      const res = await fetch(url, { headers: headers() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur téléchargement (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || `attachment-${uid}-${attachmentIndex}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error('Erreur réseau lors du téléchargement');
    }
  };

  // AI action
  const handleAIAction = async (action) => {
    if (!selectedMessage) return;
    setAiLoading(true);
    try {
      const res = await fetch(appendConn(`${API}/api/webmail/ai/analyze`), {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: selectedMessage.uid, folder: selectedFolder, action })
      });
      if (res.ok) {
        const data = await res.json();
        setAiResult({ action, ...data });
      } else {
        toast.error('Erreur IA');
      }
    } catch (err) {
      toast.error('Erreur IA');
    } finally {
      setAiLoading(false);
    }
  };

  // Reply / Forward
  const handleReply = (type, suggestedBody = '') => {
    setReplyTo(fullMessage || selectedMessage);
    setReplyType(type);
    setInitialReplyBody(suggestedBody);
    setComposeOpen(true);
  };

  const handleForward = () => {
    setReplyTo(fullMessage || selectedMessage);
    setReplyType('forward');
    setInitialReplyBody('');
    setComposeOpen(true);
  };

  // Init
  useEffect(() => { loadMailboxes(); }, []);

  // Ouverture ciblée : dossier + message demandés, une fois les boîtes connues. Lecture seule ici aussi : on n'appelle ni « marquer lu » ni l'extraction IA.
  useEffect(() => {
    const c = cibleRef.current;
    if (!boitesPretes || !c) return;
    cibleRef.current = null;
    (async () => {
      setSelectedFolder(c.dossier);
      setPage(1);
      setFullMessageLoading(true);
      try {
        const conn = c.connectionId ? `&connection_id=${encodeURIComponent(c.connectionId)}` : '';
        const res = await fetch(`${API}/api/webmail/message/${encodeURIComponent(c.uid)}?folder=${encodeURIComponent(c.dossier)}${conn}`, { headers: headers() });
        if (res.ok) {
          const data = await res.json();
          const msg = data.message || data;
          setSelectedMessage({ ...msg, uid: c.uid });
          setFullMessage(msg);
        } else {
          toast.error("Ce message n'est plus à cet endroit dans la boîte (il a peut-être été déplacé).");
        }
      } catch (err) {
        toast.error("Impossible d'ouvrir ce message dans le webmail.");
      } finally {
        setFullMessageLoading(false);
      }
    })();
  }, [boitesPretes]);
  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadMessages(selectedFolder, page); }, [selectedFolder, page, activeConnectionId]);

  // Créer un dossier IMAP (racine OU sous-dossier d'un parent)
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      const res = await fetch(appendConn(`${API}/api/webmail/folders/create`), {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFolderName.trim(), parent: newFolderParent || null })
      });
      if (res.ok) {
        toast.success(`Dossier "${newFolderName}" créé`);
        setShowCreateFolder(false);
        setNewFolderName('');
        setNewFolderParent('');
        setParentLocked(false);
        loadFolders(true);
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Erreur création dossier');
      }
    } catch (err) {
      toast.error('Erreur création dossier');
    } finally {
      setCreatingFolder(false);
    }
  };

  // Création d'un sous-dossier directement depuis le menu kebab d'un folder
  const handleCreateSubfolder = (parentFolder) => {
    setNewFolderName('');
    setNewFolderParent(parentFolder.full_path || parentFolder.name);
    setParentLocked(true);
    setShowCreateFolder(true);
  };

  // Ouvre le dialog de renommage / déplacement
  const handleRenameFolder = (folder, opts = {}) => {
    const segments = (folder.name || '').split('.');
    const leaf = segments.pop() || folder.name || '';
    const parent = segments.join('.') || '';
    setRenameDialog({
      folder,
      mode: opts.mode === 'move' ? 'move' : 'rename',
      newLeaf: leaf,
      newParent: parent,
    });
  };

  // Submit du dialog rename/move
  const submitRenameFolder = async () => {
    if (!renameDialog) return;
    const { folder, newLeaf, newParent } = renameDialog;
    const leaf = (newLeaf || '').trim();
    if (!leaf) {
      toast.error('Nom requis');
      return;
    }
    if (leaf.includes('.') || leaf.includes('/')) {
      toast.error('Le nom ne doit pas contenir "." ou "/"');
      return;
    }
    const newFullName = newParent ? `${newParent}.${leaf}` : leaf;
    if (newFullName === folder.name) {
      setRenameDialog(null);
      return;
    }
    setRenaming(true);
    try {
      const res = await fetch(
        appendConn(`${API}/api/webmail/folders/${encodeURIComponent(folder.name)}`),
        {
          method: 'PATCH',
          headers: { ...headers(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_name: newFullName }),
        },
      );
      if (res.ok) {
        toast.success(`"${folder.name}" → "${newFullName}"`);
        if (selectedFolder === folder.name) setSelectedFolder(newFullName);
        setRenameDialog(null);
        loadFolders(true);
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Opération échouée');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setRenaming(false);
    }
  };

  // Suppression via AlertDialog
  const handleDeleteFolder = (folder) => {
    setDeleteDialog(folder);
  };

  const submitDeleteFolder = async () => {
    if (!deleteDialog) return;
    const folder = deleteDialog;
    setDeleting(true);
    try {
      const res = await fetch(
        appendConn(`${API}/api/webmail/folders/${encodeURIComponent(folder.name)}`),
        { method: 'DELETE', headers: headers() },
      );
      if (res.ok) {
        toast.success(`Dossier "${folder.display_name}" supprimé`);
        if (selectedFolder === folder.name) setSelectedFolder('INBOX');
        setDeleteDialog(null);
        loadFolders(true);
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Suppression échouée (peut-être non vide)');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setDeleting(false);
    }
  };

  // Changer de boîte mail
  const switchMailbox = (boxId) => {
    setActiveConnectionId(boxId);
    setSelectedFolder('INBOX');
    setSelectedMessage(null);
    setFullMessage(null);
    setMessages([]);
  };


  // Charger signature au démarrage
  useEffect(() => {
    const loadSig = async () => {
      try {
        const res = await fetch(`${API}/api/webmail/signature`, { headers: headers() });
        if (res.ok) {
          const data = await res.json();
          if (data.signature) setSignature(data.signature);
        }
      } catch (e) { /* silencieux */ }
    };
    loadSig();
  }, [headers]);

  const handleSaveSignature = async ({ name, content, is_default }) => {
    try {
      const res = await fetch(`${API}/api/webmail/signature`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, content, is_default })
      });
      if (res.ok) {
        setSignature({ name, content, is_default });
        toast.success('Signature enregistrée');
        setSignatureModalOpen(false);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    }
  };

  // Select message
  const handleSelectMessage = (msg) => {
    setSelectedMessage(msg);
    loadFullMessage(msg.uid, selectedFolder);
  };

  // Select folder
  const handleSelectFolder = (folderName) => {
    setSelectedFolder(folderName);
    setSelectedMessage(null);
    setFullMessage(null);
    setPage(1);
    setSearchQuery('');
    setShowTriggers(false);
  };

  // Folder display name
  const currentFolderObj = folders.find(f => f.name === selectedFolder);
  const folderDisplay = currentFolderObj?.display_name || selectedFolder;

  // Compose event listener
  useEffect(() => {
    const h = () => { setReplyTo(null); setReplyType(null); setInitialReplyBody(''); setComposeOpen(true); };
    window.addEventListener('compose-email', h);
    return () => window.removeEventListener('compose-email', h);
  }, []);

  if (showTriggers) {
    return (
      <div className="flex h-full" data-testid="webmail-client">
        <FolderSidebar
          folders={folders} selectedFolder={selectedFolder} onSelectFolder={handleSelectFolder}
          loading={foldersLoading} onShowTriggers={() => setShowTriggers(!showTriggers)} showTriggersActive={showTriggers}
          onRefreshFolders={() => loadFolders(true)}
          onShowSignature={() => setSignatureModalOpen(true)} signatureActive={signatureModalOpen}
          mailboxes={mailboxes} activeConnectionId={activeConnectionId} onSwitchMailbox={switchMailbox}
          onCreateFolder={() => { setNewFolderParent(''); setParentLocked(false); setShowCreateFolder(true); }}
          onRenameFolder={handleRenameFolder}
          onDeleteFolder={handleDeleteFolder}
          onCreateSubfolder={handleCreateSubfolder}
        />
        <div className="flex-1 overflow-auto">
          <EmailTriggersManager />
        </div>
      </div>
    );
  }

  // Responsive : en mobile (<md), on n'affiche qu'une vue à la fois.
  // - Pas de message sélectionné → FolderSidebar+MessageList visibles
  //   en mode "fil d'arrivée" (sidebar empilée au-dessus de la liste).
  // - Message sélectionné → MessageView plein écran (le bouton retour
  //   `md:hidden` dans la toolbar de MessageView remet selectedMessage=null).
  // En desktop (≥md), les 3 panneaux restent côte-à-côte.
  const showSidebarMobile = !selectedMessage;
  const showListMobile = !selectedMessage;
  const showReaderMobile = !!selectedMessage;
  return (
    <div className="flex flex-col md:flex-row h-full min-h-0 overflow-hidden" data-testid="webmail-client">
      <div className={`${showSidebarMobile ? 'block' : 'hidden'} md:block`}>
        <FolderSidebar
          folders={folders} selectedFolder={selectedFolder} onSelectFolder={handleSelectFolder}
          loading={foldersLoading} onShowTriggers={() => setShowTriggers(!showTriggers)} showTriggersActive={showTriggers}
          onRefreshFolders={() => loadFolders(true)}
          onShowSignature={() => setSignatureModalOpen(true)} signatureActive={signatureModalOpen}
          mailboxes={mailboxes} activeConnectionId={activeConnectionId} onSwitchMailbox={switchMailbox}
          onCreateFolder={() => { setNewFolderParent(''); setParentLocked(false); setShowCreateFolder(true); }}
          onRenameFolder={handleRenameFolder}
          onDeleteFolder={handleDeleteFolder}
          onCreateSubfolder={handleCreateSubfolder}
        />
      </div>
      <div className={`${showListMobile ? 'flex' : 'hidden'} md:flex flex-1 md:flex-none min-w-0`}>
        <MessageList
          messages={messages} selectedMessage={selectedMessage} onSelectMessage={handleSelectMessage}
          loading={messagesLoading} folder={selectedFolder} folderDisplay={folderDisplay}
          onRefresh={() => loadMessages()} onCompose={() => setComposeOpen(true)}
          searchQuery={searchQuery} setSearchQuery={setSearchQuery} onSearch={handleSearch}
          page={page} totalMessages={totalMessages} onPageChange={setPage}
        />
      </div>
      <div className={`flex-1 min-w-0 flex ${showReaderMobile ? 'flex' : 'hidden'} md:flex`}>
      <MessageView
        message={selectedMessage} fullMessage={fullMessage} loading={fullMessageLoading}
        onBack={() => { setSelectedMessage(null); setFullMessage(null); setEmailActions(null); }}
        onReply={handleReply} onForward={handleForward} onDelete={handleDelete}
        onMarkRead={handleMarkRead} onFlag={handleFlag} onMoveMessage={handleMove}
        folders={folders} currentFolder={selectedFolder}
        onAIAction={handleAIAction} aiLoading={aiLoading} aiResult={aiResult}
        emailActions={emailActions} actionsLoading={actionsLoading}
        onExecuteAction={executeAction} onDismissActions={() => setEmailActions(null)}
        onDownloadAttachment={handleDownloadAttachment}
      />
      </div>
      <ComposeModal
        isOpen={composeOpen} onClose={() => setComposeOpen(false)} onSend={handleSend}
        replyTo={replyTo} replyType={replyType} initialBody={initialReplyBody}
        signature={signature} tenantId={tenantId}
      />
      <SignatureModal
        isOpen={signatureModalOpen} onClose={() => setSignatureModalOpen(false)}
        signature={signature} onSave={handleSaveSignature}
      />
      
      {/* Dialog création de dossier (racine ou sous-dossier) */}
      {showCreateFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-folder-dialog">
          <div className="bg-white border border-slate-300 rounded-lg p-4 w-80 space-y-3">
            <h3 className="text-sm font-semibold text-slate-800">
              {parentLocked && newFolderParent
                ? `Nouveau sous-dossier dans "${newFolderParent}"`
                : 'Créer un dossier'}
            </h3>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Nom du dossier</label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full bg-slate-100 text-sm text-slate-800 rounded px-3 py-2 border border-slate-300 focus:border-blue-500 focus:outline-none"
                placeholder="Ex: Traité, ANAH, Clients..."
                autoFocus
                data-testid="new-folder-name"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">
                Dossier parent {parentLocked ? '' : '(optionnel)'}
              </label>
              <select
                value={newFolderParent}
                onChange={(e) => setNewFolderParent(e.target.value)}
                disabled={parentLocked}
                className="w-full bg-slate-100 text-sm text-slate-800 rounded px-3 py-2 border border-slate-300 focus:border-blue-500 focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="new-folder-parent"
              >
                <option value="">Racine</option>
                {folders.map(f => (
                  <option key={f.name} value={f.full_path || f.name}>{f.display_name || f.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => { setShowCreateFolder(false); setNewFolderName(''); setNewFolderParent(''); setParentLocked(false); }}
                className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-800 transition-colors">
                Annuler
              </button>
              <button onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}
                className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded disabled:opacity-50 transition-colors flex items-center gap-1"
                data-testid="create-folder-submit">
                {creatingFolder && <Loader2 className="w-3 h-3 animate-spin" />}
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog renommer / déplacer */}
      {renameDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="rename-folder-dialog">
          <div className="bg-white border border-slate-300 rounded-lg p-4 w-96 space-y-3">
            <h3 className="text-sm font-semibold text-slate-800">
              {renameDialog.mode === 'move' ? 'Déplacer le dossier' : 'Renommer le dossier'}
              <span className="text-slate-500 font-normal"> — {renameDialog.folder.display_name}</span>
            </h3>
            {renameDialog.mode !== 'move' && (
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Nouveau nom</label>
                <input
                  type="text"
                  value={renameDialog.newLeaf}
                  onChange={(e) => setRenameDialog({ ...renameDialog, newLeaf: e.target.value })}
                  className="w-full bg-slate-100 text-sm text-slate-800 rounded px-3 py-2 border border-slate-300 focus:border-blue-500 focus:outline-none"
                  autoFocus
                  data-testid="rename-folder-leaf"
                  onKeyDown={(e) => e.key === 'Enter' && submitRenameFolder()}
                />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Dossier parent</label>
              <select
                value={renameDialog.newParent}
                onChange={(e) => setRenameDialog({ ...renameDialog, newParent: e.target.value })}
                className="w-full bg-slate-100 text-sm text-slate-800 rounded px-3 py-2 border border-slate-300 focus:border-blue-500 focus:outline-none"
                data-testid="rename-folder-parent"
                autoFocus={renameDialog.mode === 'move'}
              >
                <option value="">Racine</option>
                {folders
                  .filter(f => {
                    const path = f.full_path || f.name;
                    return path !== renameDialog.folder.name
                      && !path.startsWith(`${renameDialog.folder.name}.`);
                  })
                  .map(f => (
                    <option key={f.name} value={f.full_path || f.name}>{f.display_name || f.name}</option>
                  ))}
              </select>
            </div>
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1 border border-slate-200">
              Nouveau chemin :{' '}
              <span className="font-mono text-slate-700">
                {(renameDialog.newParent ? `${renameDialog.newParent}.` : '') + (renameDialog.newLeaf || '?')}
              </span>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setRenameDialog(null)}
                className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-800 transition-colors">
                Annuler
              </button>
              <button onClick={submitRenameFolder} disabled={renaming || !renameDialog.newLeaf.trim()}
                className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded disabled:opacity-50 transition-colors flex items-center gap-1"
                data-testid="rename-folder-submit">
                {renaming && <Loader2 className="w-3 h-3 animate-spin" />}
                {renameDialog.mode === 'move' ? 'Déplacer' : 'Renommer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AlertDialog suppression */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(open) => { if (!open) setDeleteDialog(null); }}>
        <AlertDialogContent data-testid="delete-folder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce dossier ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog && (
                <>
                  Le dossier <span className="font-mono font-semibold">{deleteDialog.display_name}</span>{' '}
                  sera supprimé définitivement. Cette action ne peut pas être annulée. Le serveur IMAP
                  refusera la suppression si le dossier n'est pas vide ou s'il contient des sous-dossiers.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submitDeleteFolder(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="delete-folder-submit"
            >
              {deleting && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NativeEmailClient;
